/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { ReactElement } from 'react';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { FormControlLabel, Typography } from '@mui/material';
import { RadioGroup, Radio } from '@mui/material';

export interface RadioGroupFilterProps<T> {
    options: ReadonlyArray<T>;
    selected: T;
    title?: string;
    direction?: 'row' | 'column';
    small?: boolean;
    getItemContent(value: T): ReactElement | string;
    onChange(value: T): void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        wrapper: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
        },
        title: {
            margin: theme.spacing(0.5, 0, 1, 0),
            fontWeight: 500,
            lineHeight: 1.4,
        },
        radio: {
            marginRight: theme.spacing(0.5),
            marginLeft: theme.spacing(0.5),
            padding: theme.spacing(0.5),
        },
        radioLabel: {
            fontSize: 14,
            wordBreak: 'break-word',
        },
        horizontalRadio: {
            padding: theme.spacing(0.5),
        },
    }),
);

export function RadioGroupFilter<T>(props: RadioGroupFilterProps<T>) {
    const classes = useStyles();
    const { title, options, selected, getItemContent, onChange, direction, small } = props;

    return (
        <div className={classes.wrapper}>
            {title ? (
                <Typography className={classes.title} variant={'subtitle1'}>
                    {title}
                </Typography>
            ) : null}

            <RadioGroup
                style={{ flexDirection: direction ? direction : 'column' }}
                value={selected}
                onChange={(e) => onChange(e.target.value as unknown as T)}
            >
                {options.map((option, index) => {
                    return (
                        <FormControlLabel
                            key={index}
                            value={option.toString()}
                            control={
                                <Radio
                                    color="primary"
                                    classes={{ root: direction === 'row' ? classes.horizontalRadio : classes.radio }}
                                    size={small ? 'small' : 'medium'}
                                />
                            }
                            classes={{ label: small ? classes.radioLabel : null }}
                            label={getItemContent(option)}
                        />
                    );
                })}
            </RadioGroup>
        </div>
    );
}
